<script setup>
import { Dialog, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {
    IconAddressBook,
    IconAlertTriangle,
    IconCalendarTime,
    IconCaravan,
    IconChartBarPopular,
    IconChevronRight,
    IconCoinEuro,
    IconFileInvoice,
    IconHistory,
    IconHome,
    IconId,
    IconKey,
    IconLogout,
    IconSettings,
    IconTerminal2,
    IconUsers,
    IconX,
} from '@tabler/icons-vue';
import { computed, ref } from 'vue';
import { useUser } from '@/composables/useUser';
import { Link, router } from '@inertiajs/vue3';
import { useSidebarStore } from '@/stores/sidebarStore.js';
import SidebarItem from '@/components/SidebarItem.vue';
import SidebarGroup from '@/components/SidebarGroup.vue';

const store = useSidebarStore();

const sidebarOpen = computed({
    get() {
        return store.open;
    },
    set(newValue) {
        store.toggleSidebar(newValue);
    },
});

const currentRoute = ref(route());

router.on('navigate', () => {
    currentRoute.value = route();
});

const navigation = computed(() => {
    return [
        {
            name: 'Home',
            href: route('dashboard'),
            icon: IconHome,
            current: currentRoute.value.current('dashboard'),
        },
        {
            name: 'Klanten',
            href: route('clients.index'),
            icon: IconAddressBook,
            current: currentRoute.value.current('clients.*'),
        },
        {
            name: 'Voertuigen',
            href: route('vehicles.index'),
            icon: IconCaravan,
            current: currentRoute.value.current('vehicles.*'),
        },
        {
            name: 'Reserveringen',
            href: route('subscriptions.index'),
            icon: IconId,
            current: currentRoute.value.current('subscriptions.*'),
        },
        {
            name: 'Afspraken',
            href: route('appointments.index'),
            icon: IconCalendarTime,
            current: currentRoute.value.current('appointments.*'),
        },
        {
            name: 'Facturen',
            href: route('invoices.index'),
            icon: IconFileInvoice,
            current: currentRoute.value.current('invoices.*'),
        },
        {
            name: 'Betalingen',
            href: route('payments.index'),
            icon: IconCoinEuro,
            current: currentRoute.value.current('payments.*'),
        },
        {
            name: 'Statistieken',
            href: route('metrics.index'),
            icon: IconChartBarPopular,
            current: currentRoute.value.current('metrics.*'),
        },
    ];
});

const adminNavigation = computed(() => {
    return [
        {
            name: 'Gebruikers',
            href: route('admin.users.index'),
            icon: IconUsers,
            current: currentRoute.value.current('admin.users.*'),
        },
        {
            name: 'Commando\'s',
            href: route('admin.commands.index'),
            icon: IconTerminal2,
            current: currentRoute.value.current('admin.commands.*'),
        },
        {
            name: 'Logs',
            icon: IconHistory,
            current:
                currentRoute.value.current('admin.activity-log.*')
                || currentRoute.value.current('admin.emails.*')
                || currentRoute.value.current('admin.request-logs.*'),
            children: [
                {
                    name: 'Activiteitenlog',
                    href: route('admin.activity-log.index'),
                    current: currentRoute.value.current('admin.activity-log.*'),
                },
                {
                    name: 'E-mails',
                    href: route('admin.emails.index'),
                    current: currentRoute.value.current('admin.emails.*'),
                },
                {
                    name: 'API-verkeer',
                    href: route('admin.request-logs.index'),
                    current: currentRoute.value.current('admin.request-logs.*'),
                },
            ],
        },
        {
            name: 'Facturatie',
            icon: IconAlertTriangle,
            current:
                currentRoute.value.current('admin.failed-invoices.*')
                || currentRoute.value.current('admin.scheduled-cancellations.*'),
            children: [
                {
                    name: 'Mislukte facturen',
                    href: route('admin.failed-invoices.index'),
                    current: currentRoute.value.current('admin.failed-invoices.*'),
                },
                {
                    name: 'Annuleringen',
                    href: route('admin.scheduled-cancellations.index'),
                    current: currentRoute.value.current('admin.scheduled-cancellations.*'),
                },
            ],
        },
        {
            name: 'API-sleutels',
            href: route('admin.api-keys.index'),
            icon: IconKey,
            current: currentRoute.value.current('admin.api-keys.*'),
        },
    ];
});

const settingsLink = computed(() => {
    return {
        name: 'Instellingen',
        href: route('settings.index'),
        icon: IconSettings,
        current: currentRoute.value.current('settings.*'),
    };
});

const user = useUser();
</script>

<template>
    <TransitionRoot
        as="template"
        :show="sidebarOpen"
    >
        <Dialog
            as="div"
            class="relative z-70 lg:hidden"
            @close="sidebarOpen = false"
        >
            <TransitionChild
                as="template"
                enter="transition-opacity ease-linear duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="transition-opacity ease-linear duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-900/80" />
            </TransitionChild>

            <div class="fixed inset-0 flex">
                <TransitionChild
                    as="template"
                    enter="transition ease-in-out duration-300 transform"
                    enter-from="-translate-x-full"
                    enter-to="translate-x-0"
                    leave="transition ease-in-out duration-300 transform"
                    leave-from="translate-x-0"
                    leave-to="-translate-x-full"
                >
                    <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
                        <TransitionChild
                            as="template"
                            enter="ease-in-out duration-300"
                            enter-from="opacity-0"
                            enter-to="opacity-100"
                            leave="ease-in-out duration-300"
                            leave-from="opacity-100"
                            leave-to="opacity-0"
                        >
                            <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                                <button
                                    type="button"
                                    class="-m-2.5 p-2.5"
                                    @click="sidebarOpen = false"
                                >
                                    <span class="sr-only">Close sidebar</span>
                                    <IconX
                                        class="size-6 text-white"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                        </TransitionChild>

                        <div class="flex grow flex-col bg-white">
                            <!-- Logo -->
                            <div class="flex shrink-0 items-center justify-center px-6 py-2">
                                <img
                                    class="h-16 w-auto"
                                    src="/images/logo-kleur.png"
                                    alt="Maat Stalling"
                                />
                            </div>

                            <!-- Navigation -->
                            <nav class="flex-1 overflow-y-auto overflow-x-hidden pb-6">
                                <ul
                                    role="list"
                                    class="flex flex-1 flex-col gap-y-7 px-6"
                                >
                                    <li>
                                        <ul
                                            role="list"
                                            class="-mx-2 space-y-1"
                                        >
                                            <li
                                                v-for="item in navigation"
                                                :key="item.name"
                                            >
                                                <Link
                                                    v-if="!item.children"
                                                    :href="item.href"
                                                    :class="[
                                                        item.current
                                                            ? 'font-semibold text-primary-600'
                                                            : 'text-gray-900 hover:bg-gray-50',
                                                        'group flex min-h-11 items-center gap-x-3 rounded-md p-2 text-sm leading-6',
                                                    ]"
                                                    @click="sidebarOpen = false"
                                                >
                                                    <component
                                                        :is="item.icon"
                                                        :class="[
                                                            item.current
                                                                ? 'text-primary-600'
                                                                : 'text-primary-400 group-hover:text-primary-600',
                                                            'size-5 shrink-0',
                                                        ]"
                                                        aria-hidden="true"
                                                    />
                                                    {{ item.name }}
                                                </Link>

                                                <Disclosure
                                                    v-else
                                                    v-slot="{ open }"
                                                    as="div"
                                                >
                                                    <DisclosureButton :class="[item.current ? 'bg-gray-50' : 'hover:bg-gray-50', 'flex w-full items-center gap-x-3 rounded-md p-2 text-left text-sm/6 font-semibold text-gray-700']">
                                                        <component
                                                            :is="item.icon"
                                                            class="size-6 shrink-0 text-gray-400"
                                                            aria-hidden="true"
                                                        />
                                                        {{ item.name }}
                                                        <IconChevronRight
                                                            :class="[open ? 'rotate-90 text-gray-500' : 'text-gray-400', 'ml-auto size-5 shrink-0']"
                                                            aria-hidden="true"
                                                        />
                                                    </DisclosureButton>
                                                    <DisclosurePanel
                                                        as="ul"
                                                        class="mt-1 px-2"
                                                    >
                                                        <li
                                                            v-for="subItem in item.children"
                                                            :key="'subitem' + subItem.name"
                                                        >
                                                            <Link
                                                                :href="subItem.href"
                                                                :class="[subItem.current ? 'bg-gray-50' : 'hover:bg-gray-50', 'block rounded-md py-2 pr-2 pl-9 text-sm/6 text-gray-700']"
                                                                @click="sidebarOpen = false"
                                                            >
                                                                {{ subItem.name }}
                                                            </Link>
                                                        </li>
                                                    </DisclosurePanel>
                                                </Disclosure>
                                            </li>
                                        </ul>
                                    </li>

                                    <li v-if="user.is_admin">
                                        <ul
                                            role="list"
                                            class="-mx-2 space-y-1"
                                        >
                                            <li
                                                class="text-xs font-semibold uppercase tracking-wider text-gray-400"
                                            >
                                                Admin
                                            </li>
                                            <li
                                                v-for="item in adminNavigation"
                                                :key="item.name"
                                            >
                                                <Link
                                                    v-if="!item.children"
                                                    :href="item.href"
                                                    :class="[
                                                        item.current
                                                            ? 'font-semibold text-primary-600'
                                                            : 'text-gray-900 hover:bg-gray-50 hover:text-black',
                                                        'group flex min-h-11 items-center gap-x-4 rounded-md p-2 text-sm leading-6',
                                                    ]"
                                                    @click="sidebarOpen = false"
                                                >
                                                    <component
                                                        :is="item.icon"
                                                        :class="[
                                                            item.current
                                                                ? 'text-primary-500'
                                                                : 'text-primary-400 group-hover:text-gray-700',
                                                            'size-5 shrink-0',
                                                        ]"
                                                        aria-hidden="true"
                                                    />
                                                    {{ item.name }}
                                                </Link>

                                                <Disclosure
                                                    v-else
                                                    v-slot="{ open }"
                                                    as="div"
                                                    :default-open="item.current"
                                                >
                                                    <DisclosureButton
                                                        :class="[
                                                            item.current
                                                                ? 'font-semibold text-primary-600'
                                                                : 'text-gray-900 hover:bg-gray-50 hover:text-black',
                                                            'group flex min-h-11 w-full items-center gap-x-4 rounded-md p-2 text-left text-sm leading-6',
                                                        ]"
                                                    >
                                                        <component
                                                            :is="item.icon"
                                                            :class="[
                                                                item.current
                                                                    ? 'text-primary-500'
                                                                    : 'text-primary-400 group-hover:text-gray-700',
                                                                'size-5 shrink-0',
                                                            ]"
                                                            aria-hidden="true"
                                                        />
                                                        {{ item.name }}
                                                        <IconChevronRight
                                                            :class="[open ? 'rotate-90 text-gray-500' : 'text-gray-400', 'ml-auto size-5 shrink-0']"
                                                            aria-hidden="true"
                                                        />
                                                    </DisclosureButton>
                                                    <DisclosurePanel
                                                        as="ul"
                                                        class="mt-1 px-2"
                                                    >
                                                        <li
                                                            v-for="subItem in item.children"
                                                            :key="'subitem' + subItem.name"
                                                        >
                                                            <Link
                                                                :href="subItem.href"
                                                                :class="[
                                                                    subItem.current
                                                                        ? 'font-semibold text-primary-600'
                                                                        : 'text-gray-900 hover:bg-gray-50 hover:text-black',
                                                                    'block rounded-md py-2 pr-2 pl-9 text-sm leading-6',
                                                                ]"
                                                                @click="sidebarOpen = false"
                                                            >
                                                                {{ subItem.name }}
                                                            </Link>
                                                        </li>
                                                    </DisclosurePanel>
                                                </Disclosure>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </nav>

                            <div
                                class="mt-auto flex shrink-0 flex-col border-t border-gray-200 px-6 py-2"
                            >
                                <Link
                                    :href="settingsLink.href"
                                    :class="[
                                        settingsLink.current
                                            ? 'font-semibold text-primary-600'
                                            : 'text-gray-900 hover:bg-gray-50 hover:text-black',
                                        'group -mx-2 flex min-h-11 items-center gap-x-4 rounded-md p-2 text-sm leading-6',
                                    ]"
                                    @click="sidebarOpen = false"
                                >
                                    <IconSettings
                                        :class="[
                                            settingsLink.current
                                                ? 'text-primary-500'
                                                : 'text-primary-400 group-hover:text-primary-600',
                                            'size-5 shrink-0',
                                        ]"
                                        aria-hidden="true"
                                    />
                                    Instellingen
                                </Link>
                                <Link
                                    :href="route('logout')"
                                    method="post"
                                    as="button"
                                    type="submit"
                                    class="group -mx-2 flex min-h-11 w-full items-center gap-x-4 rounded-md p-2 text-sm leading-6 text-gray-900 hover:bg-gray-50 hover:text-black"
                                >
                                    <IconLogout
                                        class="size-5 shrink-0 text-primary-400 group-hover:text-primary-600"
                                        aria-hidden="true"
                                    />
                                    Uitloggen
                                </Link>
                            </div>
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop -->
    <div class="hidden pt-16 lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-56 lg:flex-col">
        <div
            class="layout-sidebar bg-gray-50 flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-300"
        >
            <nav class="mt-6 flex flex-1 flex-col pb-4">
                <ul
                    role="list"
                    class="flex flex-1 flex-col gap-y-7"
                >
                    <SidebarGroup>
                        <SidebarItem
                            v-for="item in navigation"
                            :key="item.name"
                            :item="item"
                        />
                    </SidebarGroup>

                    <SidebarGroup v-if="user.is_admin">
                        <li class="px-6 text-sm font-medium leading-6 text-gray-700">
                            Admin
                        </li>

                        <SidebarItem
                            v-for="item in adminNavigation"
                            :key="item.name"
                            :item="item"
                        />
                    </SidebarGroup>

                    <SidebarGroup class="mt-auto">
                        <SidebarItem :item="settingsLink" />
                    </SidebarGroup>
                </ul>
            </nav>
        </div>
    </div>
</template>
